import React, { FC } from 'react';
import { BasicContainer, Loader } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { CountryEnum, CurrencyEnum, PlanTermEnum } from '@minecraft.graphql-operations';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { captureExceptionEvent } from '@blocs.observability';
import { CompletePurchaseLoader } from './CompletePurchaseLoader';
import { CheckoutProps } from './types';
import { Checkout as CheckoutV1 } from './Checkout';
import { CheckoutV2 } from './CheckoutV2';
import { UpgradeModalExperimentGroups } from '../enums/experimentGroups';
import { ThreeDActionType } from '../ThreeDSecureFlow';
import { usePaymentMethodForm } from '../usePaymentMethodForm';
import { ApplePayPayment, useApplePay } from '../ApplePay';

export const CheckoutWrapper: FC<CheckoutProps> = ({
  onNext,
  currentPurchase,
  onCompletePurchase,
  currencyCode,
  onApplyCoupon,
  couponLoading,
  applyCouponError,
  onRemoveCoupon,
  completePurchaseLoading,
  completePurchaseError,
  wallet,
  walletLoading,
  selectedPaymentOption,
  onSelectPaymentOption,
  addPaymentError,
  isAnnual,
  plans,
  plansError,
  plansLoading,
  setIsAnnual,
  selectedPlan,
  setSelectedPlan,
  countryCode,
  onBack,
  startingStep,
  onAddPaymentOption,
  setShowAddPaymentMethod = () => null,
  showAddPaymentMethod,
  addPaymentLoading,
  threeDActionContext,
  onThreeDActionError,
  onUpdatePaymentOption,
  isModal,
  getTitle = () => null,
  paywallLocation,
  currentPlanCode,
  enableAnnualRenewal,
  isWithinTrialPeriod,
  previewMediaPurchaseLoading,
  previewMediaPurchase,
  closeBillingPopup,
  onPaymentCompleteRedirect,
  isAnnualToggleLocked,
  currentProfile,
  modalVariant,
  discountType,
}) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const isSinglePageUpgradeModal = modalVariant === UpgradeModalExperimentGroups.VariantA;
  const Checkout = isModal && isSinglePageUpgradeModal ? CheckoutV2 : CheckoutV1;

  const {
    paymentMethodFormRef,
    paymentMethodFormikRef,
    handlePaymentPriorityChange,
    requestRecurlyTokenAndValidate: tokenizeAndSubmit,
  } = usePaymentMethodForm({ onUpdatePaymentOption });

  const { showApplePayButton, onClick: onClickApplePay } = useApplePay({
    country: countryCode as CountryEnum,
    currency: currencyCode as CurrencyEnum,
    onPaymentAuthorized: async ({ payment }: ApplePayPayment) => {
      const { recurlyToken } = payment;
      try {
        await onCompletePurchase({ token: recurlyToken.id, termsAccepted: true });
        onNext();
        showMessage({
          message: t('common:paywall.payment.success'),
          color: 'success',
        });
      } catch (error) {
        console.error('onAddPaymentOption error', error);
        throw error;
      }
    },
    onError: (error) => {
      console.error(error);
      captureExceptionEvent(error, { location: 'useAddPaymentMethods > add Apple Pay' });
    },
    currentPurchase,
    selectedPlan,
  });

  return (
    <BasicContainer w="full">
      <Loader
        loading={completePurchaseLoading || threeDActionContext?.actionType === ThreeDActionType.PURCHASE}
        LoaderComponent={<CompletePurchaseLoader />}
        error={plansError && t('common:globalError.genericUnexpected')}
      >
        <Checkout
          onNext={onNext}
          currentPurchase={currentPurchase}
          onCompletePurchase={onCompletePurchase}
          currencyCode={currencyCode}
          onApplyCoupon={onApplyCoupon}
          couponLoading={couponLoading}
          applyCouponError={applyCouponError}
          onRemoveCoupon={onRemoveCoupon}
          completePurchaseLoading={completePurchaseLoading}
          completePurchaseError={completePurchaseError}
          wallet={wallet}
          walletLoading={walletLoading}
          selectedPaymentOption={selectedPaymentOption}
          onSelectPaymentOption={onSelectPaymentOption}
          addPaymentError={addPaymentError}
          isAnnual={isAnnual}
          plans={plans}
          plansError={plansError}
          plansLoading={plansLoading}
          setIsAnnual={setIsAnnual}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          countryCode={countryCode}
          onBack={onBack}
          startingStep={startingStep}
          onAddPaymentOption={onAddPaymentOption}
          onUpdatePaymentOption={onUpdatePaymentOption}
          setShowAddPaymentMethod={setShowAddPaymentMethod}
          showAddPaymentMethod={showAddPaymentMethod}
          addPaymentLoading={addPaymentLoading}
          threeDActionContext={threeDActionContext}
          onThreeDActionError={onThreeDActionError}
          isModal={isModal}
          getTitle={getTitle}
          paywallLocation={paywallLocation}
          currentPlanCode={currentPlanCode}
          enableAnnualRenewal={enableAnnualRenewal}
          isWithinTrialPeriod={isWithinTrialPeriod}
          previewMediaPurchaseLoading={previewMediaPurchaseLoading}
          previewMediaPurchase={previewMediaPurchase}
          closeBillingPopup={closeBillingPopup}
          onPaymentCompleteRedirect={onPaymentCompleteRedirect}
          selectedPlanTerm={selectedPlan?.term?.code === PlanTermEnum.Annual ? 'annual' : 'monthly'}
          isAnnualToggleLocked={isAnnualToggleLocked}
          currentProfile={currentProfile}
          showApplePayButton={showApplePayButton}
          onClickApplePay={onClickApplePay}
          requestRecurlyTokenAndValidate={tokenizeAndSubmit}
          handlePaymentPriorityChange={handlePaymentPriorityChange}
          paymentMethodFormRef={paymentMethodFormRef}
          paymentMethodFormikRef={paymentMethodFormikRef}
          modalVariant={modalVariant}
          discountType={discountType}
        />
      </Loader>
    </BasicContainer>
  );
};
